import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { ConnectionHandler, useMutation } from 'react-relay';
import { MutationParameters, PayloadError, RecordSourceSelectorProxy } from 'relay-runtime';
import { v4 as uuidv4 } from 'uuid';

import { DEFAULT_ITEM_NAME } from '../const';
import {
    CreateScheduleItemMutation$data,
    ScheduleItemTypeInput,
} from '../graphql/__generated__/CreateScheduleItemMutation.graphql';
import CreateScheduleItemMutation from '../graphql/mutations/CreateScheduleItemMutation';
import { ComponentsDispatch } from '../store';
import { setterGantItems } from '../store/slices/gantItemsSlice';
import { changeUpdateScheduleFetchKey } from '../store/slices/settingSlice';
import { removeSpinnerItem } from '../store/slices/spinnerSlice';
import { ScheduleItemType } from '../types';

const useCommitScheduleItem = () => {
    const dispatch = useDispatch<ComponentsDispatch>();
    const [commitCreate] = useMutation(CreateScheduleItemMutation);
    const [recreateScheduleItem, setRecreateScheduleItem] = useState<{
        data: ScheduleItemTypeInput;
        connectionId: string;
    }>();

    useEffect(() => {
        if (recreateScheduleItem) {
            const [_, indexDefaultName] = recreateScheduleItem.data.name.split(`${DEFAULT_ITEM_NAME} `);
            const newItem: ScheduleItemTypeInput = {
                ...recreateScheduleItem.data,
                name: `${DEFAULT_ITEM_NAME} ${+indexDefaultName + 1}`,
            };
            commitCreate(getCommitScheduleItem(newItem, recreateScheduleItem.connectionId));
            setRecreateScheduleItem(undefined);
        }
    }, [recreateScheduleItem]);

    const updateStore = (store: any, id: string, data: ScheduleItemType) => {
        const oldScheduleItem = store.get(id);

        if (oldScheduleItem) {
            const fields: (keyof ScheduleItemType)[] = [
                'bond_left',
                'bond_right',
                'bond_parent_ss',
                'bond_parent_se',
                'bond_parent_es',
                'bond_parent_ee',
                'bond_ss',
                'bond_se',
                'bond_es',
                'bond_ee',
                'children',
                'completion_percentage',
                'start_datetime',
                'end_datetime',
                'color',
                'type',
                'status',
                'name',
                'cost_code_id',
                'number_wbs',
                'assignee',
                'division_id',
                'parent_id',
                'description',
                'phase_id',
                'project_id',
                'owner_item_id',
                'contract_invoice',
                'count_hours',
                'count_days',
                'count_people',
                'equipment_hours',
                'count_tools',
                'material_hours',
                'priority',
                'value_equipment',
                'value_labour',
                'wo_si',
                'value_material',
                'value_other',
                'value_total',
                'count_material',
            ];
            fields.forEach((field) => oldScheduleItem.setValue(data[field], field));
        }
    };

    const getCommitScheduleItem = useMemo(
        () =>
            (
                data: ScheduleItemTypeInput,
                connectionId: string,
                id?: string,
                successAdd?: (id: string) => void,
            ): MutationParameters => {
                // const optimisticUpdateScheduleItem = {
                //     updateScheduleItem: {
                //         id,
                //         ...data,
                //     },
                // };

                const optimisticUpdater = (store: any) => {
                    updateStore(store, id!, data as ScheduleItemType);
                };

                return {
                    variables: {
                        input: { ...data, id: id || data.id },
                        connections: [connectionId],
                    },
                    // optimisticResponse: id ? optimisticUpdateScheduleItem : undefined,
                    onCompleted: (response: CreateScheduleItemMutation$data, errors: PayloadError[]) => {
                        if (errors) {
                            const err = errors[0] as PayloadError & {
                                extensions: {
                                    debugMessage: string;
                                    validation: { name: string[] };
                                };
                            };
                            console.log(
                                err.extensions.debugMessage,
                                errors,
                                err.extensions.validation.name[0],
                                data.name.includes(DEFAULT_ITEM_NAME),
                            );
                            if (
                                err.extensions.validation.name[0] === 'The name has already been taken.' &&
                                data.name.includes(DEFAULT_ITEM_NAME)
                            ) {
                                setRecreateScheduleItem({ data, connectionId });
                            }
                        } else {
                            console.log('ScheduleItem save successful', response, id);
                            if (id) {
                                dispatch(changeUpdateScheduleFetchKey());
                            }

                            if (!id) {
                                successAdd?.(response.createScheduleItem?.node?.id!);
                            }
                        }

                        dispatch(changeUpdateScheduleFetchKey());
                    },
                    optimisticUpdater: id ? optimisticUpdater : undefined,
                    onError: (error: any) => {
                        console.error('Mutation error:', error);
                    },
                } as any as MutationParameters;
            },
        [],
    );

    const getCommitBulkScheduleItem = useMemo(
        () =>
            (dataList: ScheduleItemTypeInput[], connectionId: string): MutationParameters => {
                const optimisticUpdateScheduleItem = {
                    updateBulkScheduleItem: dataList,
                };

                const optimisticUpdater = (store: any) => {
                    dataList.forEach((data) => {
                        updateStore(store, data.id!, data as ScheduleItemType);
                    });
                };

                return {
                    variables: {
                        input: dataList,
                        connections: [connectionId],
                    },
                    optimisticResponse: optimisticUpdateScheduleItem,
                    onCompleted: (response: CreateScheduleItemMutation$data, errors: PayloadError[]) => {
                        if (errors) {
                            console.log(
                                (
                                    errors[0] as PayloadError & {
                                        extensions: { debugMessage: string };
                                    }
                                ).extensions.debugMessage,
                                errors,
                            );
                        } else {
                            dispatch(removeSpinnerItem('savingProcess'));
                            console.log('ScheduleItem save successful', response);

                            if (dataList.length) {
                                dispatch(changeUpdateScheduleFetchKey());
                            }
                        }
                    },
                    updater: optimisticUpdater,
                    onError: (error: any) => {
                        console.error('Mutation error:', error);
                    },
                } as any as MutationParameters;
            },
        [],
    );

    const getCommitScheduleItemDelete = (
        id: string | string[],
        connectionId: string,
        successDelete?: (id?: string | string[]) => void,
    ): MutationParameters => {
        return {
            variables: {
                id,
                listId: id,
                connections: [connectionId],
            },
            updater: (store: RecordSourceSelectorProxy) => {
                const payload = store.getRootField('deleteScheduleItem');
                if (!payload) return;

                const deletedItem = payload.getLinkedRecord('deletedItem');
                if (!deletedItem) return;

                const deletedItemId = deletedItem.getValue('id');

                const connection = store.get(connectionId);
                if (connection && deletedItemId) {
                    ConnectionHandler.deleteNode(connection, deletedItemId as string);
                    setTimeout(() => {
                        dispatch(setterGantItems({ wasChanged: false }));
                        dispatch(changeUpdateScheduleFetchKey());
                    });
                }
            },
            onCompleted: (response: any, errors: PayloadError[]) => {
                if (errors) {
                    console.log(
                        (
                            errors[0] as PayloadError & {
                                extensions: { debugMessage: string };
                            }
                        ).extensions.debugMessage,
                        errors,
                    );
                } else {
                    const {
                        deleteScheduleItem: { deletedItem },
                    } = response;

                    console.log('ScheduleItem delete successful', deletedItem?.id);

                    successDelete?.(id);
                    dispatch(setterGantItems({ externalTableScheduleItemPrefixFetchKey: uuidv4() }));
                }
            },
            onError: (error: any) => {
                console.error('Mutation error:', error);
            },
        } as any as MutationParameters;
    };

    const getCommitScheduleBulkItemDelete = (
        ids: string[],
        connectionId: string,
        successDelete?: (id?: string | string[]) => void,
    ): MutationParameters => {
        return {
            variables: {
                listId: ids,
            },
            updater: (store: RecordSourceSelectorProxy) => {
                const connection = store.get(connectionId);
                if (connection) {
                    ids.forEach((deletedId) => {
                        ConnectionHandler.deleteNode(connection, deletedId);
                    });

                    setTimeout(() => {
                        dispatch(setterGantItems({ wasChanged: false }));
                        dispatch(changeUpdateScheduleFetchKey());
                    });
                }
            },
            onCompleted: (response: any, errors: PayloadError[]) => {
                if (errors) {
                    console.log(
                        (
                            errors[0] as PayloadError & {
                                extensions: { debugMessage: string };
                            }
                        ).extensions.debugMessage,
                        errors,
                    );
                } else {
                    const deletedIds: string[] = response.deleteBulkScheduleItem ?? [];

                    console.log('ScheduleItems delete successful', deletedIds);

                    successDelete?.(deletedIds);
                    dispatch(setterGantItems({ externalTableScheduleItemPrefixFetchKey: uuidv4() }));
                }
            },
            onError: (error: any) => {
                console.error('Mutation error:', error);
            },
        } as any as MutationParameters;
    };

    return {
        getCommitScheduleItem,
        getCommitScheduleItemDelete,
        getCommitScheduleBulkItemDelete,
        getCommitBulkScheduleItem,
    };
};

export default useCommitScheduleItem;
